import React, { useState, useEffect } from 'react';
import { X, Package, User, Home, FileText, Save, Search, Plus, MapPin, Truck, Printer } from 'lucide-react';
import '../styles/ConsignmentEntryFormLT.css';
import apiClient from '../services/api';
import { printConsignment } from '../utils/printUtils';

const ConsignmentEntryFormD = ({ onClose, onSave, editData = null }) => {
  const CONSIGNMENT_TYPE = 'D'; // Domestic
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [employees, setEmployees] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cities, setCities] = useState([]);
  const [generatingNumber, setGeneratingNumber] = useState(false); // Track generation state
  
  // Customer Selection Modal States
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerModalType, setCustomerModalType] = useState('');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showAddCustomerForm, setShowAddCustomerForm] = useState(false);
  
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  
  const [formData, setFormData] = useState({
    ConsignmentNo: '',
    ConsignmentDate: new Date().toISOString().slice(0, 16),
    ConsignmentType: CONSIGNMENT_TYPE,
    
    // Route
    OriginCityID: '',
    DestinationCityID: '',
    
    // Shipper
    ShipperID: '',
    ShipperCode: '',
    ShipperName: '',
    ShipperContactPerson: '',
    ShipperFlatBuilding: '',
    ShipperStreet: '',
    ShipperPhone: '',
    ShipperCity: '',
    
    // Consignee
    ConsigneeID: '',
    ConsigneeCode: '',
    ConsigneeName: '',
    ConsigneeContactPerson: '',
    ConsigneeFlatBuilding: '',
    ConsigneeStreet: '',
    ConsigneePhone: '',
    ConsigneeCity: '',
    
    // Package Details
    Description: '',
    NoOfPkgs: '',
    Weight: '',
    Pieces: '',
    InvoiceValue: '',
    CourierType: '',
    PaymentMode: '',
    
    // Transport & Delivery
    DriverID: '',
    CourierStatusID: '',
    DeliveryDate: '',
    CollectedBy: '',
    ReceivedBy: '',
    Remarks: ''
  });

  const [newCustomerData, setNewCustomerData] = useState({
    customername: '',
    customertype: 'CR',
    phone: '',
    email: '',
    address: '',
    cityid: '',
    taxnumber: ''
  });

  useEffect(() => {
    const initializeForm = async () => {
      await loadDropdownData();
      
      if (editData && editData.ConsignmentID) {
        setFormData({...editData, ConsignmentType: CONSIGNMENT_TYPE});
      } else {
        setGeneratingNumber(true);
        await generateConsignmentNumber();
        setGeneratingNumber(false);
      }
    };
    
    initializeForm();
  }, [editData]);

  const loadDropdownData = async () => {
    try {
      // Load all required data
      const [empRes, driverRes, custRes, cityRes] = await Promise.all([
        apiClient.get('/Employees.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/Drivers.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/Customers.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/Cities.php').catch(e => ({ data: { success: false } }))
      ]);

      // Set data from responses
      if (empRes.data.success) setEmployees(empRes.data.data || []);
      if (custRes.data.success) setCustomers(custRes.data.data || []);
      if (cityRes.data.success) setCities(cityRes.data.data || []);
      
      if (driverRes.data.success) {
        const allDrivers = driverRes.data.data || [];
        const activeDrivers = allDrivers.filter(d => d.isactive === 1);
        setDrivers(activeDrivers);
      } else {
        setDrivers([]);
      }
    } catch (err) {
      console.error('Error loading dropdown data:', err);
      setError('Failed to load dropdown data');
    }
  };

  const generateConsignmentNumber = async () => {
    try {
      console.log('🔢 Generating domestic consignment number...');
      const response = await apiClient.get(`/ConsignmentsD.php/next-number?type=${CONSIGNMENT_TYPE}`);
      
      if (response.data.success && response.data.data && response.data.data.consignmentNo) {
        const newNumber = response.data.data.consignmentNo;
        console.log('✅ Generated:', newNumber);
        
        setFormData(prev => ({
          ...prev,
          ConsignmentNo: newNumber
        }));
        
        return newNumber;
      } else {
        throw new Error('Invalid API response');
      }
    } catch (err) {
      console.error('❌ Error generating number:', err);
      const timestamp = Date.now();
      const fallbackNumber = `CNS-D-${timestamp}`;
      console.log('⚠️ Using fallback:', fallbackNumber);
      
      setFormData(prev => ({ 
        ...prev, 
        ConsignmentNo: fallbackNumber 
      }));
      
      return fallbackNumber;
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openCustomerModal = (type) => {
    setCustomerModalType(type);
    setShowCustomerModal(true);
    setCustomerSearchTerm('');
  };

  const selectCustomer = (customer) => {
    if (customerModalType === 'shipper') {
      setFormData(prev => ({
        ...prev,
        ShipperID: customer.customerid,
        ShipperCode: customer.customercode,
        ShipperName: customer.customername,
        ShipperPhone: customer.phone || '',
        ShipperCity: customer.cityname || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        ConsigneeID: customer.customerid,
        ConsigneeCode: customer.customercode,
        ConsigneeName: customer.customername,
        ConsigneePhone: customer.phone || '',
        ConsigneeCity: customer.cityname || ''
      }));
    }
    setShowCustomerModal(false);
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    
    if (!newCustomerData.customername.trim()) {
      alert('Customer name is required');
      return;
    }

    try {
      const response = await apiClient.post('/Customers.php', {
        ...newCustomerData,
        companyid: companyId,
        isactive: 1
      });
      
      if (response.data.success) {
        await loadDropdownData();
        
        const newCustomer = {
          customerid: response.data.data.customerid,
          customercode: response.data.data.customercode,
          customername: newCustomerData.customername,
          phone: newCustomerData.phone,
          cityname: cities.find(c => c.cityid === parseInt(newCustomerData.cityid))?.cityname || ''
        };
        
        selectCustomer(newCustomer);
        
        setNewCustomerData({
          customername: '',
          customertype: 'CR',
          phone: '',
          email: '',
          address: '',
          cityid: '',
          taxnumber: ''
        });
        setShowAddCustomerForm(false);
        alert(`Customer created!\nCode: ${response.data.data.customercode}`);
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (err) {
      console.error('Error adding customer:', err);
      alert('Error adding customer: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.ConsignmentNo.trim()) {
      setError('Consignment number is required');
      return;
    }
    
    if (!formData.ShipperName.trim()) {
      setError('Shipper name is required');
      return;
    }
    
    if (!formData.ConsigneeName.trim()) {
      setError('Consignee name is required');
      return;
    }
    
    if (!formData.OriginCityID) {
      setError('Origin city is required');
      return;
    }
    
    if (!formData.DestinationCityID) {
      setError('Destination city is required');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const dataToSave = {
        ...formData,
        ConsignmentType: CONSIGNMENT_TYPE,
        ShipperID: formData.ShipperID || null,
        ConsigneeID: formData.ConsigneeID || null,
        DriverID: formData.DriverID || null,
        CollectedBy: formData.CollectedBy || null,
        ReceivedBy: formData.ReceivedBy || null,
        CourierStatusID: formData.CourierStatusID || null,
        OriginCityID: formData.OriginCityID || null,
        DestinationCityID: formData.DestinationCityID || null,
        NoOfPkgs: formData.NoOfPkgs || 0,
        Weight: formData.Weight || 0,
        Pieces: formData.Pieces || 0,
        InvoiceValue: formData.InvoiceValue || 0
      };
      
      // ✅ Include ConsignmentID if editing
      if (editData && editData.ConsignmentID) {
        dataToSave.ConsignmentID = editData.ConsignmentID;
      }
      
      await onSave(dataToSave);
    } catch (err) {
      setError(err.message || 'Failed to save consignment');
      console.error('Save error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.customername?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    c.customercode?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    c.phone?.includes(customerSearchTerm)
  );

  if (!companyId) {
    return (
      <div className="consignment-form-overlay">
        <div className="consignment-form-container" style={{ maxWidth: '600px' }}>
          <div className="form-header">
            <h2>Session Required</h2>
            <button className="close-btn" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <p>Please select a company and financial year from the dashboard.</p>
            <button onClick={onClose} className="btn btn-primary" style={{ marginTop: '16px' }}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="consignment-form-overlay">
      <div className="consignment-form-container" style={{ maxWidth: '1200px' }}>
        {/* ✅ WHITE TEXT HEADER - Same style as Land Transport */}
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          padding: '28px 32px',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '4px solid #059669',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flex: 1 }}>
            <div style={{
              width: '60px',
              height: '60px',
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
            }}>
              <Home size={24} color="#ffffff" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                flexWrap: 'wrap'
              }}>
                <div style={{ 
                  color: '#ffffff',
                  fontSize: '1.8rem',
                  fontWeight: '700',
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                }}>
                  {editData ? 'Edit' : 'New'} Consignment - Domestic (D)
                </div>
                <div style={{
                  color: '#ffffff',
                  fontSize: '1rem',
                  fontWeight: '600',
                  background: 'rgba(255,255,255,0.25)',
                  padding: '6px 14px',
                  borderRadius: '6px',
                  display: 'inline-block',
                  border: '1px solid rgba(255,255,255,0.4)',
                  fontFamily: '"Courier New", monospace'
                }}>
                  📋 {generatingNumber ? 'Generating...' : (formData.ConsignmentNo || 'Loading...')}
                </div>
              </div>
            </div>
          </div>
          <button 
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              color: '#ffffff',
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            }}
          >
            <X size={20} color="#ffffff" />
          </button>
        </div>

        {error && (
          <div className="alert alert-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-content">
            
            {/* Basic Information */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title">
                <FileText size={18} />
                Basic Information (Domestic)
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Consignment No *</label>
                  <input
                    type="text"
                    name="ConsignmentNo"
                    value={generatingNumber ? 'Loading...' : formData.ConsignmentNo}
                    readOnly
                    placeholder={generatingNumber ? "Generating number..." : ""}
                    style={{ 
                      background: generatingNumber ? '#fff3cd' : '#d1fae5', 
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      color: generatingNumber ? '#856404' : '#000'
                    }}
                  />
                </div>
                <div className="form-group">
                  <label>Consignment Date & Time *</label>
                  <input
                    type="datetime-local"
                    name="ConsignmentDate"
                    value={formData.ConsignmentDate}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Route: Origin & Destination */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title" style={{ background: '#d1fae5', padding: '8px 12px', borderRadius: '6px' }}>
                <MapPin size={18} />
                Consignment Route
              </h3>
              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="form-group">
                  <label>Origin (Pickup City) *</label>
                  <select
                    name="OriginCityID"
                    value={formData.OriginCityID}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Origin City --</option>
                    {cities.map(city => (
                      <option key={city.cityid} value={city.cityid}>
                        {city.cityname} {city.statename && `(${city.statename})`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Destination (Delivery City) *</label>
                  <select
                    name="DestinationCityID"
                    value={formData.DestinationCityID}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Destination City --</option>
                    {cities.map(city => (
                      <option key={city.cityid} value={city.cityid}>
                        {city.cityname} {city.statename && `(${city.statename})`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Shipper Information */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title">
                <User size={18} />
                Shipper | Sender
                <button
                  type="button"
                  className="btn-sm btn-primary"
                  onClick={() => openCustomerModal('shipper')}
                  style={{ marginLeft: 'auto' }}
                >
                  <Search size={14} />
                  Select Customer
                </button>
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Customer Code</label>
                  <input
                    type="text"
                    name="ShipperCode"
                    value={formData.ShipperCode}
                    readOnly
                    style={{ background: '#f3f4f6' }}
                  />
                </div>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="ShipperName"
                    value={formData.ShipperName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Contact Person</label>
                  <input
                    type="text"
                    name="ShipperContactPerson"
                    value={formData.ShipperContactPerson}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    name="ShipperPhone"
                    value={formData.ShipperPhone}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Building</label>
                  <input
                    type="text"
                    name="ShipperFlatBuilding"
                    value={formData.ShipperFlatBuilding}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Street</label>
                  <input
                    type="text"
                    name="ShipperStreet"
                    value={formData.ShipperStreet}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="ShipperCity"
                    value={formData.ShipperCity}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            {/* Consignee Information */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title">
                <User size={18} />
                Consignee | Receiver
                <button
                  type="button"
                  className="btn-sm btn-primary"
                  onClick={() => openCustomerModal('consignee')}
                  style={{ marginLeft: 'auto' }}
                >
                  <Search size={14} />
                  Select Customer
                </button>
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Customer Code</label>
                  <input
                    type="text"
                    name="ConsigneeCode"
                    value={formData.ConsigneeCode}
                    readOnly
                    style={{ background: '#f3f4f6' }}
                  />
                </div>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="ConsigneeName"
                    value={formData.ConsigneeName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Contact Person</label>
                  <input
                    type="text"
                    name="ConsigneeContactPerson"
                    value={formData.ConsigneeContactPerson}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    name="ConsigneePhone"
                    value={formData.ConsigneePhone}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Building</label>
                  <input
                    type="text"
                    name="ConsigneeFlatBuilding"
                    value={formData.ConsigneeFlatBuilding}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Street</label>
                  <input
                    type="text"
                    name="ConsigneeStreet"
                    value={formData.ConsigneeStreet}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="ConsigneeCity"
                    value={formData.ConsigneeCity}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            {/* Package Details */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title">
                <Package size={18} />
                Package Details
              </h3>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    name="Description"
                    value={formData.Description}
                    onChange={handleChange}
                    rows="2"
                  />
                </div>
                <div className="form-group">
                  <label>Weight (kg)</label>
                  <input
                    type="number"
                    step="0.001"
                    name="Weight"
                    value={formData.Weight}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Pieces</label>
                  <input
                    type="number"
                    name="Pieces"
                    value={formData.Pieces}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>No. of Packages</label>
                  <input
                    type="number"
                    name="NoOfPkgs"
                    value={formData.NoOfPkgs}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Invoice Value</label>
                  <input
                    type="number"
                    step="0.01"
                    name="InvoiceValue"
                    value={formData.InvoiceValue}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Courier Type</label>
                  <input
                    type="text"
                    name="CourierType"
                    value={formData.CourierType}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Payment Mode</label>
                  <select
                    name="PaymentMode"
                    value={formData.PaymentMode}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    <option value="Cash">Cash</option>
                    <option value="Credit">Credit</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Transport & Delivery Details */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title">
                <Truck size={18} />
                Transport & Delivery Details
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Driver {drivers.length === 0 && '(No active drivers)'}</label>
                  <select
                    name="DriverID"
                    value={formData.DriverID}
                    onChange={handleChange}
                  >
                    <option value="">Select Driver</option>
                    {drivers.map(driver => (
                      <option key={driver.driverid} value={driver.driverid}>
                        {driver.drivername}
                        {driver.phone && ` - ${driver.phone}`}
                        {driver.licenseno && ` | License: ${driver.licenseno}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Collected By</label>
                  <select
                    name="CollectedBy"
                    value={formData.CollectedBy}
                    onChange={handleChange}
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.employeeid} value={emp.employeeid}>
                        {emp.employeename}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Received By</label>
                  <select
                    name="ReceivedBy"
                    value={formData.ReceivedBy}
                    onChange={handleChange}
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.employeeid} value={emp.employeeid}>
                        {emp.employeename}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    name="CourierStatusID"
                    value={formData.CourierStatusID}
                    onChange={handleChange}
                  >
                    <option value="">Select Status</option>
                    <option value="1">Pending</option>
                    <option value="2">In Transit</option>
                    <option value="3">Delivered</option>
                    <option value="4">Returned</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Delivery Date</label>
                  <input
                    type="date"
                    name="DeliveryDate"
                    value={formData.DeliveryDate}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Remarks</label>
                  <textarea
                    name="Remarks"
                    value={formData.Remarks}
                    onChange={handleChange}
                    rows="2"
                  />
                </div>
              </div>
            </div>

          </div>

          <div className="form-footer">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            
            {editData && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => printConsignment(formData, CONSIGNMENT_TYPE)}
                disabled={loading}
                style={{
                  background: '#10b981',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Printer size={18} />
                Print Consignment
              </button>
            )}
            
            <button
              type="submit"
              className="btn btn-save"
              disabled={loading || generatingNumber}
              style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
            >
              {loading ? (
                <>
                  <div className="btn-spinner" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Domestic Consignment
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Customer Selection Modal */}
      {showCustomerModal && (
        <div className="modal-overlay" onClick={() => setShowCustomerModal(false)}>
          <div className="modal-container" style={{ maxWidth: '800px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Select {customerModalType === 'shipper' ? 'Shipper' : 'Consignee'}</h3>
              <button onClick={() => setShowCustomerModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="search-box" style={{ marginBottom: '16px' }}>
                <Search size={20} />
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={customerSearchTerm}
                  onChange={(e) => setCustomerSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>

              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={() => setShowAddCustomerForm(!showAddCustomerForm)}
                style={{ marginBottom: '16px' }}
              >
                <Plus size={16} />
                Add New Customer
              </button>

              {showAddCustomerForm && (
                <div style={{ marginBottom: '16px', padding: '16px', background: '#f9fafb', borderRadius: '8px' }}>
                  <h4 style={{ marginBottom: '12px' }}>New Customer</h4>
                  <form onSubmit={handleAddCustomer}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div className="form-group">
                        <label>Name *</label>
                        <input
                          type="text"
                          value={newCustomerData.customername}
                          onChange={(e) => setNewCustomerData({...newCustomerData, customername: e.target.value})}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Phone</label>
                        <input
                          type="text"
                          value={newCustomerData.phone}
                          onChange={(e) => setNewCustomerData({...newCustomerData, phone: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>City</label>
                        <select
                          value={newCustomerData.cityid}
                          onChange={(e) => setNewCustomerData({...newCustomerData, cityid: e.target.value})}
                        >
                          <option value="">Select City</option>
                          {cities.map(city => (
                            <option key={city.cityid} value={city.cityid}>{city.cityname}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      <button type="submit" className="btn-primary btn-sm">Create & Select</button>
                      <button type="button" className="btn-secondary btn-sm" onClick={() => setShowAddCustomerForm(false)}>Cancel</button>
                    </div>
                  </form>
                </div>
              )}

              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>City</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomers.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                          No customers found
                        </td>
                      </tr>
                    ) : (
                      filteredCustomers.map(customer => (
                        <tr key={customer.customerid}>
                          <td>{customer.customercode}</td>
                          <td>{customer.customername}</td>
                          <td>{customer.phone}</td>
                          <td>{customer.cityname}</td>
                          <td>
                            <button
                              type="button"
                              className="btn-primary btn-sm"
                              onClick={() => selectCustomer(customer)}
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsignmentEntryFormD;